import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ChartLineUp, ChatCircleDots, Ticket, Timer, Trophy, Tag, CaretDown,
} from "@phosphor-icons/react";

const fmtTime = (s) => {
  if (!s || s <= 0) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const RANGES = [
  { d: 1, label: "Hoje" },
  { d: 7, label: "7 dias" },
  { d: 30, label: "30 dias" },
  { d: 90, label: "90 dias" },
];

export default function CompanyReports() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data: rep } = await api.get(`/reports/overview?days=${d}`);
      setData(rep);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <div className="p-8 max-w-7xl" data-testid="reports-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Análise</p>
          <h1 className="text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
            <ChartLineUp size={32} weight="duotone" /> Relatórios
          </h1>
          <p className="text-sm text-[#5C5C59] mt-2">Volume, SLA e desempenho por atendente.</p>
        </div>
        <div className="flex gap-1" data-testid="reports-range">
          {RANGES.map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              data-testid={`range-${r.d}`}
              className={`px-3 py-1.5 border text-xs font-semibold ${days === r.d ? "border-[#0F4C3A] bg-[#0F4C3A] text-white" : "border-[#E5E5E0] hover:border-[#0F4C3A]"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-[#5C5C59]">Carregando…</div>}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Kpi label="Mensagens recebidas" value={data.messages.in} icon={ChatCircleDots} color="#0F4C3A" testid="kpi-in" />
            <Kpi label="Mensagens enviadas" value={data.messages.out} icon={ChatCircleDots} color="#2563EB" testid="kpi-out" />
            <Kpi label="Tickets novos" value={data.tickets.new} icon={Ticket} color="#D97706" testid="kpi-new" />
            <Kpi label="Tickets resolvidos" value={data.tickets.resolved} icon={Ticket} color="#0F4C3A" testid="kpi-resolved" />
          </div>

          {/* SLA */}
          <section className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-[#5C5C59] flex items-center gap-1">
                <Timer size={12} weight="fill" /> Tempo médio 1ª resposta
              </p>
              <p className="text-4xl font-black tracking-tighter mt-2 text-[#0F4C3A]">{fmtTime(data.sla.avg_first_response_seconds)}</p>
              <p className="text-xs text-[#5C5C59] mt-1">{data.sla.samples_first_response} tickets analisados</p>
            </div>
            <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-[#5C5C59] flex items-center gap-1">
                <Timer size={12} weight="fill" /> Tempo médio de resolução
              </p>
              <p className="text-4xl font-black tracking-tighter mt-2 text-[#0F4C3A]">{fmtTime(data.sla.avg_resolution_seconds)}</p>
              <p className="text-xs text-[#5C5C59] mt-1">{data.sla.samples_resolution} tickets resolvidos</p>
            </div>
          </section>

          {/* Daily volume */}
          <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5 mb-8">
            <h2 className="text-sm font-black uppercase tracking-wider mb-4">Volume diário</h2>
            <DailyChart daily={data.daily || []} />
          </section>

          {/* Per attendant + top tags */}
          <section className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
              <h2 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                <Trophy size={16} weight="duotone" /> Ranking de atendentes
              </h2>
              {(data.per_attendant || []).length === 0 && <p className="text-xs text-[#5C5C59]">Nenhum envio no período.</p>}
              <ol className="space-y-2">
                {data.per_attendant.map((a, i) => (
                  <li key={a.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className={`text-xs font-mono w-6 text-center ${i < 3 ? "text-[#D97706] font-bold" : "text-[#5C5C59]"}`}>#{i + 1}</span>
                      <span className="font-semibold">{a.name}</span>
                    </span>
                    <span className="font-mono text-[#0F4C3A] font-bold">{a.count}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5">
              <h2 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                <Tag size={16} weight="duotone" /> Top tags
              </h2>
              {(data.top_tags || []).length === 0 && <p className="text-xs text-[#5C5C59]">Nenhuma tag usada no período.</p>}
              <ul className="space-y-2">
                {data.top_tags.map((t) => (
                  <li key={t.tag} className="flex items-center justify-between text-sm">
                    <span className="font-semibold">#{t.tag}</span>
                    <span className="font-mono text-[#0F4C3A]">{t.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Ticket status distribution */}
          {Object.keys(data.tickets.by_status || {}).length > 0 && (
            <section className="mt-8 bg-white border border-[#E5E5E0] shadow-brutal p-5">
              <h2 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                <CaretDown size={16} weight="bold" /> Distribuição de tickets
              </h2>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(data.tickets.by_status).map(([k, v]) => (
                  <div key={k} className="p-3 bg-[#F7F7F5]">
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-[#5C5C59]">{k}</p>
                    <p className="text-2xl font-black mt-1">{v}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-white border border-[#E5E5E0] shadow-brutal p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase font-semibold tracking-wider text-[#5C5C59]">{label}</p>
        <Icon size={18} weight="duotone" style={{ color }} />
      </div>
      <p className="text-3xl font-black tracking-tighter mt-2" style={{ color }}>{value}</p>
    </div>
  );
}

function DailyChart({ daily }) {
  if (!daily || daily.length === 0) return <p className="text-xs text-[#5C5C59]">Sem dados no período.</p>;
  const max = Math.max(1, ...daily.map((d) => Math.max(d.in || 0, d.out || 0)));
  return (
    <div className="flex items-end gap-1 h-32">
      {daily.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.day}: ${d.in} in, ${d.out} out`}>
          <div className="flex items-end gap-0.5 h-24 w-full justify-center">
            <div
              className="w-[6px] bg-[#0F4C3A] transition-all"
              style={{ height: `${((d.in || 0) / max) * 100}%` }}
              title={`Recebidas: ${d.in || 0}`}
            />
            <div
              className="w-[6px] bg-[#2563EB] transition-all"
              style={{ height: `${((d.out || 0) / max) * 100}%` }}
              title={`Enviadas: ${d.out || 0}`}
            />
          </div>
          <span className="text-[9px] font-mono text-[#5C5C59] whitespace-nowrap">{d.day.slice(5)}</span>
        </div>
      ))}
      <div className="ml-3 flex flex-col justify-end gap-1 text-[10px] text-[#5C5C59]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#0F4C3A]" /> In</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#2563EB]" /> Out</span>
      </div>
    </div>
  );
}
